import { useMutation, useQueryClient } from "@tanstack/react-query";
import { bulkWriteIt, updateTileStatus } from "@/services/review.service";
import { BulkWriteItRequest } from "@/types/review.types";
import { ReviewTileItem } from "@/types/tile.types";
import { toast } from "sonner";
import {
  FailedTile,
  useBulkWriteItProgress,
} from "@/taskpane/components/BulkWriteItProgressContext";
import { useTileTextExtraction } from "./useTileTextExtraction";

interface BulkWriteItResult {
  successCount: number;
  failedCount: number;
  currentFailedTiles: FailedTile[];
}

export const useBulkWriteIt = () => {
  const queryClient = useQueryClient();
  const { updateProgress, completeProgress, resetProgress } = useBulkWriteItProgress();
  const { getSearchableText, normalizeSearchText } = useTileTextExtraction();

  const extractSuggestedClause = (tile: ReviewTileItem): string | null => {
    const originalData = tile.originalData;
    if (!originalData) return null;

    return (
      originalData.suggestedclause ||
      originalData.suggestion ||
      originalData.suggestions ||
      originalData.suggestedNumber ||
      originalData.additionalClause ||
      originalData.missingClause ||
      null
    );
  };

  const formatTileText = (tile: ReviewTileItem): string => {
    let text = "";
    if (tile.clause) text += `**${tile.clause}**\n\n`;
    if (tile.reason) text += `**Reason:** ${tile.reason}\n\n`;
    if (tile.analysis) text += `**Analysis:** ${tile.analysis}\n\n`;
    if (tile.explanation) text += `**Explanation:** ${tile.explanation}\n\n`;
    return text.trim();
  };

  const performClientSideInsertion = async (
    items: ReviewTileItem[],
    documentId: string
  ): Promise<BulkWriteItResult> => {
    let successCount = 0;
    let failedCount = 0;
    const currentFailedTiles: FailedTile[] = [];
    const successfulTileUpdates: { [queryTerm: string]: number[] } = {};
    const failedTileUpdates: { [queryTerm: string]: number[] } = {};

    const totalTiles = items.length;

    for (const [index, item] of items.entries()) {
      try {
        await Word.run(async (context) => {
          const searchableText = getSearchableText(item);
          const normalizedText = normalizeSearchText(searchableText);

          if (!normalizedText) {
            throw new Error("No searchable text found for this tile");
          }

          // Truncate to 255 chars for search to avoid API errors, similar to useTileSearch
          let truncatedText = normalizedText;
          if (truncatedText.length > 255) {
            truncatedText = truncatedText.substring(0, 255);
          }

          const searchOptions = {
            ignorePunct: true,
            ignoreSpace: true,
            matchCase: false,
            matchWholeWord: false,
          };

          const searchResults = context.document.body.search(truncatedText, searchOptions);
          context.load(searchResults, "items");
          await context.sync();

          if (searchResults.items.length === 0) {
            // Fallback: Try splitting by delimiters if full search fails (similar to useTileSearch)
            const delimiters = [":", ";"];
            let found = false;

            for (const delimiter of delimiters) {
              if (truncatedText.includes(delimiter)) {
                const parts = truncatedText.split(delimiter);
                const firstPart = parts[0]?.trim();
                if (firstPart) {
                  const fallbackResults = context.document.body.search(firstPart, searchOptions);
                  context.load(fallbackResults, "items");
                  await context.sync();

                  if (fallbackResults.items.length > 0) {
                    // Found with fallback, use the first match
                    const range = fallbackResults.items[0];

                    if (range) {
                      // Determine what to replace WITH
                      const suggestedClause = extractSuggestedClause(item);
                      let textToInsert: string;
                      if (suggestedClause) {
                        textToInsert = suggestedClause;
                      } else {
                        textToInsert = formatTileText(item);
                      }

                      range.insertText(textToInsert, Word.InsertLocation.replace);
                      await context.sync();
                      found = true;
                      break;
                    }
                  }
                }
              }
            }

            if (!found) {
              throw new Error("Original text not found in document");
            }
          } else {
            // Found match(es)
            // Replace the FIRST occurrence found.
            const range = searchResults.items[0];

            if (range) {
              const suggestedClause = extractSuggestedClause(item);
              let textToInsert: string;

              if (suggestedClause) {
                textToInsert = suggestedClause;
              } else {
                textToInsert = formatTileText(item);
              }

              range.insertText(textToInsert, Word.InsertLocation.replace);
              await context.sync();
            }
          }
        });

        successCount++;
        if (item.query) {
          if (!successfulTileUpdates[item.query]) successfulTileUpdates[item.query] = [];
          if (item.originalData?.tile_id !== undefined) {
            successfulTileUpdates[item.query]?.push(item.originalData.tile_id);
          }
        }
      } catch (error: any) {
        console.error(`Error processing tile ${item.id}:`, error);
        failedCount++;
        currentFailedTiles.push({
          id: item.id,
          text: item.clause || item.id || "Unknown Tile",
          reason: error.message || "Unknown error",
        });
        if (item.query) {
          if (!failedTileUpdates[item.query]) failedTileUpdates[item.query] = [];
          if (item.originalData?.tile_id !== undefined) {
            failedTileUpdates[item.query]?.push(item.originalData.tile_id);
          }
        }
      }

      const currentProgress = ((index + 1) / totalTiles) * 100;
      updateProgress(
        currentProgress,
        `Processing tile ${index + 1} of ${totalTiles}...`,
        currentFailedTiles
      );
    }

    // Update statuses
    for (const queryTerm in successfulTileUpdates) {
      const tileIds = successfulTileUpdates[queryTerm];
      if (tileIds && tileIds.length > 0) {
        await updateTileStatus({
          document_id: [documentId],
          query_term: [queryTerm],
          tile_ids: tileIds,
          isCompleted: true,
          isFailed: false,
        });
      }
    }
    for (const queryTerm in failedTileUpdates) {
      const tileIds = failedTileUpdates[queryTerm];
      if (tileIds && tileIds.length > 0) {
        await updateTileStatus({
          document_id: [documentId],
          query_term: [queryTerm],
          tile_ids: tileIds,
          isCompleted: false,
          isFailed: true,
        });
      }
    }

    return { successCount, failedCount, currentFailedTiles };
  };

  return useMutation<BulkWriteItResult, Error, { items: ReviewTileItem[]; documentId: string }>({
    mutationFn: async ({ items, documentId }) => {
      resetProgress();

      // 1. Trigger API (Fire and Forget / Non-blocking for UI)
      const bulkWriteItRequest: BulkWriteItRequest = {
        document_id: [documentId],
        tile_ids: items.map((item) => item.originalData.tile_id.toString()),
        query_term: items
          .map((item) => item.query)
          .filter((query): query is string => query !== undefined),
      };

      // We don't await this to block the UI, but we should probably trigger it.
      // However, react-query mutationFn expects a promise.
      // If we want it non-blocking, we can just not await it inside the main flow,
      // OR we can await it but know it returns quickly?
      // The user said: "api won't block the operation we're currently doing its for charging on backend only."
      // This implies we should start it and not wait for it to finish before doing the client-side work.
      // But we probably want to ensure it's sent.
      bulkWriteIt(bulkWriteItRequest).catch((err) =>
        console.error("Background bulk write API failed:", err)
      );

      // 2. Perform Client-Side Insertion
      return await performClientSideInsertion(items, documentId);
    },
    onSuccess: ({ successCount, failedCount, currentFailedTiles }) => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      completeProgress(currentFailedTiles);
      if (failedCount > 0) {
        toast.error(
          `Bulk write completed with issues: ${successCount} successful, ${failedCount} failed.`
        );
      } else {
        toast.success(`Bulk write completed: ${successCount} successful.`);
      }
    },
    onError: (error: Error) => {
      console.error("Bulk write operation failed:", error);
      toast.error("Bulk write operation failed", { description: error.message });
      completeProgress([]);
    },
  });
};
